import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";

const AUDIENCE = [
  "State agency executives",
  "Chief information officers",
  "Program directors",
  "Operations leaders",
  "Product executives",
  "Technology leaders",
];

const BELIEFS = [
  {
    n: "01",
    title: "Systems, not silos.",
    desc: "Outcomes cross organizational boundaries. So must diagnosis.",
  },
  {
    n: "02",
    title: "Calm over hype.",
    desc: "We name what is happening plainly — including when the honest answer is unglamorous.",
  },
  {
    n: "03",
    title: "Evidence travels.",
    desc: "Findings are documented so your team can retrace every step without us.",
  },
  {
    n: "04",
    title: "Proof, then scale.",
    desc: "Small verified improvements before large programs. Always.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About — Improve Systems"
        siteName="Improve Systems"
        description="Improve Systems is a product and transformation advisory — helping organizations understand what needs to change before they invest in changing it, and keeping business intent and technology implementation connected."
      />
      <PageHero
        eyebrow="ABOUT"
        title="We work where outcomes depend on many things working together."
        lede="Improve Systems is a product and transformation advisory for organizations improving or replacing complex systems — especially where mission, procurement, vendors, and users must stay connected."
        testid="about-hero"
      />

      <section data-testid="about-story" className="border-b border-line bg-white">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 py-20 sm:py-28 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
                01 — WHY WE EXIST
              </p>
              <h2 className="font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
                We own the gap.
              </h2>
              <div className="mt-8 space-y-6 leading-relaxed text-slate-600">
                <p>
                  Modernization efforts rarely fail because the technology was impossible.
                  They fail in the gap between what an organization needs and what gets
                  built — between agency need, procurement requirements, vendor
                  interpretation, implementation, and the outcome users actually experience.
                </p>
                <p>
                  We exist to own that gap. The organization understands its mission; the
                  implementation vendor understands how to build and configure technology.
                  Improve Systems makes sure those two sides stay connected — from discovery
                  through delivery and measurement.
                </p>
                <p>
                  We are deliberately method-agnostic. AI, prototyping, analytics, and
                  automation are tools we bring in when they help — they are not our
                  identity. Our loyalty is to the outcome, not to any category of solution.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.12}>
              <figure className="border border-line bg-canvas p-3">
                <img
                  src="https://images.unsplash.com/photo-1614595737476-42487331b8a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwY29uY3JldGUlMjBmYWNhZGV8ZW58MHx8fHwxNzg5ODk0MDkzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Minimal concrete architectural facade — structure that holds because the system behind it was understood"
                  className="aspect-[4/3] w-full object-cover grayscale"
                  loading="lazy"
                />
                <figcaption className="flex items-center justify-between px-2 pb-1 pt-3 font-mono text-[10px] tracking-[0.25em] text-slate-400">
                  <span>[FIG.01] — STRUCTURE</span>
                  <span>UNDERSTOOD SYSTEMS HOLD</span>
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-beliefs" className="border-b border-line">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <Reveal>
            <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
              02 — WHAT WE BELIEVE
            </p>
          </Reveal>
          <div className="mt-4 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {BELIEFS.map((b, i) => (
              <Reveal key={b.n} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col bg-canvas p-7">
                  <span className="font-mono text-[11px] text-slate-400">{b.n}</span>
                  <h3 className="mt-5 font-serif text-xl leading-snug text-ink">{b.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-audience" className="border-b border-line bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-28">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="mb-6 font-mono text-[11px] tracking-[0.3em] text-slate-500">
                  03 — WHO THIS IS FOR
                </p>
                <h2 className="font-serif text-3xl leading-tight tracking-tight text-ink sm:text-4xl">
                  People accountable for outcomes.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <div className="border-t border-line">
                {AUDIENCE.map((role, i) => (
                  <Reveal key={role} delay={i * 0.05}>
                    <div className="flex items-baseline gap-6 border-b border-line py-4">
                      <span className="font-mono text-[11px] text-slate-400">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="text-base text-ink sm:text-lg">{role}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <p className="mt-8 max-w-xl text-sm leading-relaxed text-slate-600">
                  If you are responsible for an operation whose outcomes depend on many
                  interconnected parts — and the visible fixes are not holding — this practice
                  was built for you.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
